/**
 * Audio utilities for Pomodoro timer.
 * - Tick/alarm: Web Audio API (no files needed)
 * - Background music: HTMLAudioElement (mp3 from R2)
 */

let audioCtx: AudioContext | null = null

function getCtx(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext()
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume()
  }
  return audioCtx
}

/** Volume curve — logarithmic for natural perception */
function curve(v: number): number {
  const n = Math.min(1, Math.max(0, v / 100))
  return n * n // quadratic curve: 50% slider = 25% actual
}

/** Short tick */
export function playTickSound(vol: number = 30): void {
  try {
    const ctx = getCtx()
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(800, t)
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.02)
    g.gain.setValueAtTime(curve(vol) * 0.5, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05)
    osc.connect(g); g.connect(ctx.destination)
    osc.start(t); osc.stop(t + 0.05)
  } catch { /* silent */ }
}

/** Bell chime */
export function playBellAlarm(vol: number = 80): void {
  try {
    const ctx = getCtx()
    const t = ctx.currentTime
    const v = curve(vol)
    const freqs = [523.25, 659.25, 783.99, 1046.5]
    for (let i = 0; i < freqs.length; i++) {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      const s = t + i * 0.15
      osc.type = 'sine'
      osc.frequency.value = freqs[i]
      g.gain.setValueAtTime(0, s)
      g.gain.linearRampToValueAtTime(v * 0.4, s + 0.01)
      g.gain.exponentialRampToValueAtTime(0.001, s + 0.8)
      osc.connect(g); g.connect(ctx.destination)
      osc.start(s); osc.stop(s + 0.8)
    }
  } catch { /* silent */ }
}

/** Digital beep */
export function playDigitalAlarm(vol: number = 80): void {
  try {
    const ctx = getCtx()
    const t = ctx.currentTime
    const v = curve(vol)
    for (let r = 0; r < 3; r++) {
      for (let i = 0; i < 2; i++) {
        const osc = ctx.createOscillator()
        const g = ctx.createGain()
        const s = t + r * 0.6 + i * 0.15
        osc.type = 'square'
        osc.frequency.value = 880
        g.gain.setValueAtTime(v * 0.2, s)
        g.gain.setValueAtTime(0, s + 0.1)
        osc.connect(g); g.connect(ctx.destination)
        osc.start(s); osc.stop(s + 0.1)
      }
    }
  } catch { /* silent */ }
}

/** Gentle fade tone */
export function playGentleAlarm(vol: number = 80): void {
  try {
    const ctx = getCtx()
    const t = ctx.currentTime
    const v = curve(vol)
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 440
    g.gain.setValueAtTime(0, t)
    g.gain.linearRampToValueAtTime(v * 0.3, t + 0.5)
    g.gain.linearRampToValueAtTime(v * 0.3, t + 1.5)
    g.gain.exponentialRampToValueAtTime(0.001, t + 2.5)
    osc.connect(g); g.connect(ctx.destination)
    osc.start(t); osc.stop(t + 2.5)
  } catch { /* silent */ }
}

/** Play alarm by type (vol 0-100) */
export function playAlarm(type: 'bell' | 'digital' | 'gentle' | 'none', vol: number = 80): void {
  if (type === 'bell') playBellAlarm(vol)
  else if (type === 'digital') playDigitalAlarm(vol)
  else if (type === 'gentle') playGentleAlarm(vol)
}

// ── Background music (mp3) ──

let bgAudio: HTMLAudioElement | null = null

export function playBackgroundAudio(url: string, vol: number = 80): void {
  // Agar ayni shu url ijroda bo'lsa — boshidan boshlash
  if (bgAudio && bgAudio.dataset.url === url && !bgAudio.paused) {
    bgAudio.currentTime = 0
    return
  }
  stopBackgroundAudio()
  bgAudio = new Audio()
  bgAudio.dataset.url = url
  bgAudio.loop = true
  bgAudio.volume = curve(vol)
  bgAudio.preload = 'none'
  bgAudio.src = url
  bgAudio.play().catch(() => { /* autoplay blocked */ })
}

export function stopBackgroundAudio(): void {
  if (bgAudio) {
    bgAudio.pause()
    bgAudio.removeAttribute('src')
    bgAudio.load()
    bgAudio = null
  }
}

export function setBackgroundVolume(vol: number): void {
  if (bgAudio) bgAudio.volume = curve(vol)
}

export function isBackgroundPlaying(): boolean {
  return bgAudio !== null && !bgAudio.paused
}

export function getCurrentAudioUrl(): string | null {
  return bgAudio?.dataset.url ?? null
}
